import { api } from "./client";
import type { components } from "./contract";
import { unwrap } from "./errors";

type V2Asset = components["schemas"]["V2Asset"];

export interface AssetPhoneUploadSession {
  id: string;
  status: "pending" | "receiving" | "ready" | "completed" | "expired" | "failed";
  expires_at: string;
  token: string;
  asset_id?: string;
}

export interface AssetPhoneUploadStatus {
  id: string;
  status: AssetPhoneUploadSession["status"];
  expires_at: string;
  asset_id?: string;
}

export interface AssetUploadOptions {
  referenceParentAssetID?: string;
  editParentAssetID?: string;
  editOperation?: "adjust" | "resize";
  visibility: V2Asset["visibility"];
  rightsAttested: boolean;
  rightsBasis?:
    | "owned_or_licensed"
    | "consented_speaker"
    | "synthetic_fixture";
  signal?: AbortSignal;
}

/** Reserve, upload, and freeze one Asset through the canonical custody flow. */
export async function uploadAsset(
  file: File,
  options: AssetUploadOptions,
): Promise<V2Asset> {
  options.signal?.throwIfAborted();
	// Browsers commonly leave GLB MIME empty. The API still probes the bytes
	// before publication; the extension only supplies upload metadata.
  const mimeType = (!file.type || file.type === "application/octet-stream") && /\.glb$/i.test(file.name)
    ? "model/gltf-binary"
    : file.type;
  const reservation = unwrap(
    await api.POST("/v1/assets/uploads", {
      signal: options.signal,
      body: {
        file_name: file.name,
        mime_type: mimeType,
        size_bytes: file.size,
        visibility: options.visibility,
        rights_attested: options.rightsAttested,
        ...(options.referenceParentAssetID ? { reference_parent_asset_id: options.referenceParentAssetID } : {}),
        ...(options.editParentAssetID ? { edit_parent_asset_id: options.editParentAssetID, edit_operation: options.editOperation } : {}),
        ...(options.rightsAttested && options.rightsBasis
          ? { rights_basis: options.rightsBasis }
          : {}),
      },
    }),
  );
  options.signal?.throwIfAborted();
  unwrap(
    await api.PUT("/v1/assets/uploads/{id}/content", {
      signal: options.signal,
      params: { path: { id: reservation.asset.id } },
      headers: { "Content-Type": mimeType },
      body: file as unknown as string,
      bodySerializer: (body) => body as unknown as BodyInit,
    }),
  );
  options.signal?.throwIfAborted();
  return completeAssetUpload(reservation.asset.id, options.signal);
}

export async function completeAssetUpload(assetID: string, signal?: AbortSignal): Promise<V2Asset> {
  return unwrap(
    await api.POST("/v1/assets/uploads/{id}/complete", {
      signal,
      params: { path: { id: assetID } },
    }),
  );
}

export async function createAssetPhoneUpload(): Promise<AssetPhoneUploadSession> {
  return unwrap(await api.POST("/v1/asset-phone-uploads"));
}

export async function getAssetPhoneUpload(
  sessionID: string,
): Promise<AssetPhoneUploadStatus> {
  return unwrap(
    await api.GET("/v1/asset-phone-uploads/{id}", {
      params: { path: { id: sessionID } },
    }),
  );
}

export async function getPublicAssetPhoneUpload(
  token: string,
): Promise<AssetPhoneUploadStatus> {
  return unwrap(
    await api.GET("/v1/phone-uploads/{token}", {
      params: { path: { token } },
    }),
  );
}

export async function uploadPublicAssetPhoneFile(
  token: string,
  file: File,
): Promise<AssetPhoneUploadStatus> {
  return unwrap(
    await api.PUT("/v1/phone-uploads/{token}/content", {
      params: {
        path: { token },
        query: { file_name: file.name },
      },
      headers: { "Content-Type": file.type },
      body: file as unknown as string,
      bodySerializer: (body) => body as unknown as BodyInit,
    }),
  );
}
